/**
 * WalletCard Component
 * Flutter source: wallet_screen.dart - flexibleSpaceAppBar()
 *
 * Displays the wallet card in the flexible header area with:
 * - Link Revolut button (top-left)
 * - "VIRTUAL" label (top-right)
 * - USD amount display (center)
 * - Diamonds + Coins balance (bottom-left)
 * - Withdraw + Buy Coins buttons (bottom-right)
 *
 * All payment actions show InfoSheet since web doesn't support payments yet.
 */
import React from 'react';
import type { WalletEntity } from '@/domain/entities/wallet/walletEntity';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { MathUtils } from '@/domain/utils/mathUtils';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface WalletCardProps {
  wallet: WalletEntity | null;
  exchangeRate?: number | null;
  onActionTap: () => void;
}

export const WalletCard: React.FC<WalletCardProps> = ({ wallet, exchangeRate, onActionTap }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const coins = wallet?.coins ?? 0;
  const diamonds = wallet?.diamonds ?? 0;

  const usdAmount = MathUtils.diamondsToUSD(diamonds);
  const useLocal = exchangeRate != null && !!wallet?.currency;
  const displayAmount = useLocal ? usdAmount * exchangeRate! : usdAmount;
  // Flutter falls back to `RemoteConfigService.defaultCurrency` when the wallet
  // has no local currency (wallet_screen.dart:462).
  const displayCurrency = useLocal ? wallet!.currency! : remoteConfigService.defaultCurrency;

  return (
    <div
      style={{
        padding: responsive.spacing.lg,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: responsive.walletCard.maxWidth,
          backgroundColor: theme.danger,
          borderRadius: responsive.borderRadius.lg,
          padding: responsive.spacing.base,
          display: 'flex',
          flexDirection: 'column',
          gap: responsive.gap.lg,
          minHeight: responsive.walletCard.minHeight,
          justifyContent: 'space-between',
        }}
      >
        {/* Top row: Link Revolut + VIRTUAL */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span
            style={{
              color: theme.white,
              fontSize: responsive.font.sm,
              cursor: 'pointer',
              opacity: 0.9,
            }}
            onClick={onActionTap}
            {...qaAttr({ id: 'wallet-link-revolut' })}
          >
            {l10n.wallet.linkRevolut}
          </span>
          <span
            style={{
              color: theme.white,
              fontSize: responsive.font.sm,
              fontWeight: responsive.fontWeight.medium,
              opacity: 0.7,
            }}
          >
            {l10n.wallet.virtual}
          </span>
        </div>

        {/* Center: USD amount */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline' }}>
          <span
            style={{
              color: theme.white,
              fontSize: responsive.font['3xl'],
              fontWeight: responsive.fontWeight.bold,
            }}
          >
            {MathUtils.formatIntegerPart(displayAmount)}
          </span>
          <span
            style={{
              color: theme.white,
              fontSize: responsive.font.lg,
              fontWeight: responsive.fontWeight.medium,
              opacity: 0.8,
            }}
          >
            {MathUtils.getDecimalDisplay(displayAmount)}{' '}
            <span {...qaAttr({ id: 'wallet-currency', value: displayCurrency })}>
              {displayCurrency}
            </span>
          </span>
        </div>

        {/* Bottom row: Diamonds + Coins | Withdraw + Buy */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.sm }}>
            <span style={{ color: theme.white, fontSize: responsive.font.base, fontWeight: responsive.fontWeight.medium }}>
              {MathUtils.removeTrailingZero(diamonds)}
            </span>
            <AssetIcon
              path="/assets/images/diamond.png"
              width={responsive.icon.small}
              height={responsive.icon.small}
              color={theme.exclusive}
            />
            <span
              {...qaAttr({ id: 'wallet-balance', value: coins })}
              style={{ color: theme.white, fontSize: responsive.font.base, fontWeight: responsive.fontWeight.medium, marginLeft: responsive.spacing.sm }}
            >
              {MathUtils.removeTrailingZero(coins)}
            </span>
            <AssetIcon
              path="/assets/images/coins.png"
              width={responsive.icon.small}
              height={responsive.icon.small}
              color={theme.exclusive}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.lg }}>
            <div style={{ cursor: 'pointer' }} onClick={onActionTap} {...qaAttr({ id: 'wallet-action-subtract' })}>
              <AssetIcon
                path="/assets/images/substract.png"
                width={responsive.icon.medium}
                height={responsive.icon.medium}
                color={theme.white}
              />
            </div>
            <div style={{ cursor: 'pointer' }} onClick={onActionTap} {...qaAttr({ id: 'wallet-action-add' })}>
              <AssetIcon
                path="/assets/images/add.png"
                width={responsive.icon.medium}
                height={responsive.icon.medium}
                color={theme.white}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
